import { Suspense, useEffect, useMemo, useState } from 'react';

import Particles from '@tsparticles/react';
import { useMachine } from '@xstate/react';
//import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useForm } from 'react-hook-form';
import useResizeObserver from 'use-resize-observer';

import {
  Form,
  UserIcon,
  KeyIcon,
  FormMessage,
  LoginError,
  LoginWrapper,
  Logo,
} from './Login.style';
import { Button } from '../../components/shared/Button/Button';
import Input from '../../components/shared/Input/Input';
import Footer from '../../components/shared/Layouts/Footer';
import { Loading } from '../../components/shared/Loading';
import { NotificationLoading } from '../../components/shared/ui/AnimatedText';
import { API } from '../../config/API';
import { publicWindow } from '../../config/public';
import { useUserConfiguration } from '../../config/themes';
import { saveVersion } from '../../config/Version';
import { useAuthDispatch } from '../../context/AuthProvider';
import { LoginMachine } from '../../machines/LoginMachine/LoginMachine';
import BranchSelection from '../BranchPage/BranchSelection';

let { normalLoginEnabled } = publicWindow;
const { loginLabelButton } = publicWindow;
const { ssoLabelButton } = publicWindow;
const { ssoEnabled } = publicWindow;
const { ssoSn } = publicWindow;
const { ssoRedirectUrl } = publicWindow;
const redirectUrl =
  'https://sso.softonecloud.com/' + `${ssoSn}` + '?redirectUrl=' + `${ssoRedirectUrl}`;

if (ssoEnabled === false && normalLoginEnabled === false) {
  normalLoginEnabled = true;
}

const LoginPage = () => {
  const [, setIssuer] = useState('');
  //console.log(issuer);
  const redirectToAnotherPage = () => {
    window.location.href = redirectUrl;
  };

  const getQueryStringToken = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const tokenFromQuery = urlParams.get('token');

    return tokenFromQuery || ''; // Return an empty string if token is undefined
  };

  useEffect(() => {
    const token = getQueryStringToken();
    if (token) {
      const decoded = jwtDecode<{ UserId: string; Issuer: string }>(token);
      setIssuer(decoded.Issuer);
      localStorage.setItem('issuer', decoded.Issuer);
      send({ type: 'LOGIN', username: decoded.UserId, password: `SSO:${token}` });
    } else {
      // Handle the case when the token is not present
    }
  }, []);

  const { appId = '2006' } = publicWindow;
  const { title, loginWelcomeMessage, logoPath, theme } = useUserConfiguration();
  const { loginGifPath, loginGifSize, loginGifMobileSize } = theme;
  const { ref, width } = useResizeObserver();

  const authSend = useAuthDispatch();
  const [state, send] = useMachine(LoginMachine, {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    context: { API, appId },
    actions: {
      goToHome: (ctx: any) => {
        authSend({
          type: 'AUTHORIZE',
          user: {
            appId: ctx.appId,
            username: ctx.username,
            clientID: ctx.clientID,
            loginClientID: ctx.loginClientID,
          },
          branch: {
            objs: ctx.objs,
            selected: ctx.authParams,
          },
        });
        saveVersion();
      },
    },
  });
  const { value, context } = state as any;

  const { register, handleSubmit, formState } = useForm({
    shouldUnregister: false,
  });

  const onSubmit = (data: any) => {
    send({ type: 'LOGIN', username: data.user, password: data.pass });
  };
  const onBranchSubmit = (e: any) => {
    e.preventDefault();
    send({ type: 'SUBMIT_BRANCH' });
  };

  return (
    <Suspense fallback={<Loading />}>
      <LoginWrapper>
        <div className='leftContainer'>
          <h3
            dangerouslySetInnerHTML={{
              __html: loginWelcomeMessage,
            }}
          />
        </div>
        <div className='rightContainer'>
          {(() => {
            if (
              value === 'success' ||
              value === 'logginIn' ||
              value === 'authenticating'
            ) {
              return (
                <Form resize={(width ?? 0) < 650} className='loadingForm'>
                  <FormMessage color='tertiary'>{title}</FormMessage>
                  <NotificationLoading
                    showText={value === 'success'}
                    textTitle={'WELCOME'}
                  />
                </Form>
              );
            }
            if (value === 'selectingBranch' || value === 'branchFailure') {
              return (
                <Form
                  resize={(width ?? 0) < 650}
                  onSubmit={onBranchSubmit}
                  data-cy='branch-form'
                  className='branchForm'
                >
                  <Logo
                    className='header-logo'
                    alt='app-logo'
                    src={logoPath}
                    $logoHeight='80px'
                  />
                  <BranchSelection actor={context.branchActor} login />
                  <Button style={{ width: '100%' }} type='submit'>
                    CONTINUE
                  </Button>
                  {context.error && <LoginError>{context.error}</LoginError>}
                </Form>
              );
            }
            return (
              <div className='rightContentWrapper'>
                {loginGifPath && (
                  <div
                    className='loginGifContainer'
                    style={
                      {
                        '--desktop-size': loginGifSize,
                        '--mobile-size': loginGifMobileSize,
                      } as React.CSSProperties
                    }
                  >
                    <img src={loginGifPath} alt='Login Animation' className='loginGif' />
                  </div>
                )}
                <Form
                  resize={(width ?? 0) < 650}
                  onSubmit={handleSubmit(onSubmit)}
                  data-cy='login-form'
                  className='loginForm'
                >
                  <Logo
                    className='header-logo'
                    alt='app-logo'
                    src={logoPath}
                    $logoHeight='80px'
                  />
                  <Input
                    data-cy='login-username'
                    $errorMessage='Username is required'
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    error={formState.errors.user}
                    type='text'
                    icon={UserIcon}
                    label='Username'
                    placeholder='Enter your username...'
                    {...register('user', { required: true })}
                  />
                  <Input
                    data-cy='login-password'
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    error={formState.errors.pass}
                    label={'Password'}
                    icon={KeyIcon}
                    type='password'
                    placeholder='Enter your password...'
                    {...register('pass', { required: true })}
                    $errorMessage='Password is required'
                  />
                  {normalLoginEnabled && (
                    <Button style={{ width: '100%' }} type='submit'>
                      {loginLabelButton}
                    </Button>
                  )}
                  {ssoEnabled && (
                    <Button style={{ width: '100%' }} onClick={redirectToAnotherPage}>
                      {ssoLabelButton}
                    </Button>
                  )}
                  {context.error && (
                    <LoginError data-cy='login-error'>{context.error}</LoginError>
                  )}
                </Form>
              </div>
            );
          })()}
          <Footer screen='login' />
        </div>
      </LoginWrapper>
    </Suspense>
  );
};

export default LoginPage;
